/**
 * Le rotte, costruite dall'elenco delle schermate.
 *
 * Sono dichiarate **a codice e non da file** per una ragione precisa: le rotte
 * di reparto sono poche e fisse, e vederle tutte insieme accanto ai ruoli che
 * le aprono vale più della generazione automatica. Nascono da `SCHERMATE`, così
 * aggiungerne una è una riga sola e il menu la mostra da sé.
 *
 * Perché un router e non uno `useState`: le notifiche devono poter **portare
 * dove serve**, e un avviso che dice «l'ordine 605533 è fermo» senza poterci
 * andare è metà del lavoro. In più l'URL diventa un posto: si ricarica senza
 * perdere la schermata, si tiene aperta una postazione su un indirizzo fisso, e
 * si può dire a qualcuno *«apri /prelievi»* invece di spiegargli dove cliccare.
 */

import { createRootRoute, createRoute, createRouter, redirect } from '@tanstack/react-router';

import { ruoliCorrenti } from '@errebi/sessione';

import { Guscio, NonPermessa } from './shell';
import { DettaglioCassetta } from './screens/crate-detail';
import { DettaglioCarrello } from './screens/trolley-detail';
import { SCHERMATE, schermatePermesse } from './screens/list';

/**
 * La radice non è più il guscio, ed è una distinzione che serve.
 *
 * Il guscio chiede una sessione e disegna il menu: giusto per le schermate di
 * reparto, sbagliato per la pagina che si apre inquadrando il QR sul foglio di
 * una cassetta. Quella la apre chi ha un telefono in mano accanto a un
 * carrello, e una schermata di accesso al posto del contenuto renderebbe il
 * quadrato inutile. Quindi: radice nuda, guscio come strato **sotto** di lei,
 * e la pagina pubblica appesa alla radice.
 */
const radice = createRootRoute();

/** Lo strato con menu e sessione: tutte le schermate di reparto stanno qui. */
const guscio = createRoute({ getParentRoute: () => radice, id: 'guscio', component: Guscio });

/**
 * La radice porta alla prima schermata che il ruolo può aprire.
 *
 * Non a una fissa: un'operatrice di produzione che apre `/` non deve atterrare
 * sul banco di ricezione e vedersi dire che non è cosa sua.
 */
const casa = createRoute({
  getParentRoute: () => guscio,
  path: '/',
  beforeLoad: () => {
    const prima = schermatePermesse(ruoliCorrenti())[0];
    // `throw redirect(...)` è il modo con cui TanStack Router dirotta una
    // rotta: non è un errore, ed è la sua API documentata.
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    if (prima) throw redirect({ to: prima.percorso });
  },
  component: () => null,
});

const schermate = SCHERMATE.map((s) =>
  createRoute({
    getParentRoute: () => guscio,
    path: s.percorso,
    component: () => {
      // Il controllo vero resta il server, che risponde 403: questo evita solo
      // di disegnare una schermata che poi non potrebbe fare niente.
      const permessa = schermatePermesse(ruoliCorrenti()).some((x) => x.k === s.k);
      return permessa ? s.c() : <NonPermessa nome={s.nome} />;
    },
  }),
);

/**
 * Il dettaglio di una cassetta, fuori dal guscio e fuori dal menu.
 *
 * Il percorso è corto di proposito: finisce dentro un QR, e ogni carattere in
 * più è un modulo in più da stampare su ventidue millimetri di carta.
 */
const cassetta = createRoute({
  getParentRoute: () => radice,
  path: '/c/$id',
  component: function Cassetta() {
    const { id } = cassetta.useParams();
    return <DettaglioCassetta id={id} />;
  },
});

/**
 * La stessa pagina, aperta dal banco toccando il nome di una cassetta.
 *
 * Percorso separato e non un parametro in più su `/c/$id`: quello finisce
 * stampato dentro un QR e ogni carattere è un modulo di carta, questo lo scrive
 * solo un `href`. Sta fuori dal guscio come la gemella — si apre in una scheda a
 * parte, e il menu del banco lì non serve.
 */
const cassettaPerCodice = createRoute({
  getParentRoute: () => radice,
  path: '/cassetta/$codice',
  component: function CassettaPerCodice() {
    const { codice } = cassettaPerCodice.useParams();
    return <DettaglioCassetta cassetta={codice} />;
  },
});

/**
 * Il contenuto di un carrello, aperto dal nome sulla tessera dello scarico.
 *
 * **Dentro il guscio**, al contrario delle due gemelle qui sopra. Quelle stanno
 * fuori perché sono pubbliche: le apre chi inquadra un QR con un telefono e una
 * sessione non ce l'ha. Questa la apre il magazzino da una schermata di
 * reparto, e dentro il guscio sessione e ruolo arrivano da sé — la rotta
 * dall'altra parte pretende `magazzino.chiama-prelievo` come tutte quelle dello
 * scarico, e senza guscio la scheda nuova risponderebbe 401 invece di chiedere
 * chi sei.
 *
 * Non sta in `elenco.tsx` perché non è una voce di menu: non è un posto dove si
 * lavora, è il contenuto di una tessera. Il menu resta disegnato attorno, e
 * nessuna voce risulta corrente — che è esattamente com'è.
 */
const carrello = createRoute({
  getParentRoute: () => guscio,
  path: '/carrello/$k',
  component: function Carrello() {
    const { k } = carrello.useParams();
    return <DettaglioCarrello carrello={k} />;
  },
});

export const router = createRouter({
  routeTree: radice.addChildren([
    guscio.addChildren([casa, ...schermate, carrello]),
    cassetta,
    cassettaPerCodice,
  ]),
  defaultNotFoundComponent: () => <NonPermessa nome="questa pagina" />,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
